import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Alert, AlertDescription } from './ui/alert'
import { 
  Mail, 
  CreditCard
} from 'lucide-react'

/**
 * Registration Page Component
 * 
 * Handles user registration with email-only signup and Stripe checkout integration.
 * 
 * @features
 * - Email-only registration (password set via email activation)
 * - Affiliate tracking via UTM parameters from marketing site
 * - SessionStorage management for affiliate codes
 * - Automatic redirect to Stripe checkout
 * - Optional 14-day free trial via ?trial=true query parameter
 * 
 * @trialParameter
 * - Regular signup: /register (no free trial)
 * - Trial signup: /register?trial=true (includes 14-day free trial)
 * - UI and backend behavior adapts based on trial parameter
 * 
 * @affiliateTracking
 * - Captures `utm_source` from URL query parameters
 * - Cleans code (alphanumeric, uppercase) for Stripe compatibility
 * - Stores in sessionStorage as fallback (cleared if no UTM in URL)
 * - Sends affiliate code to backend for coupon auto-application
 * - Falls back gracefully if affiliate code doesn't apply
 */
const RegisterPage = () => {
  const [searchParams] = useSearchParams()
  const [formData, setFormData] = useState({
    email: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [affiliateCode, setAffiliateCode] = useState(null)
  
  // Check if free trial should be included (via ?trial=true query parameter)
  const includeTrial = searchParams.get('trial') === 'true'

  /**
   * Capture and store affiliate tracking from UTM parameters
   * 
   * Cleans UTM source parameter and stores for checkout session.
   * Clears sessionStorage if no UTM present to avoid stale codes.
   */
  useEffect(() => {
    // Get UTM source from URL parameters
    const utmSource = searchParams.get('utm_source')
    
    if (utmSource && utmSource !== 'direct') {
      // Clean the affiliate code (alphanumeric, uppercase)
      const cleanedCode = utmSource.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
      
      // Store in sessionStorage as fallback
      sessionStorage.setItem('utm_source', cleanedCode)
      setAffiliateCode(cleanedCode)
      
      console.log('Affiliate code captured from URL:', cleanedCode)
    } else {
      // No UTM in URL - clear any stale sessionStorage to avoid using old codes
      sessionStorage.removeItem('utm_source')
      setAffiliateCode(null)
      console.log('No affiliate code in URL - cleared sessionStorage')
    }
  }, [searchParams])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleStripeCheckout = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Basic validation
    if (!formData.email) {
      setError('Email is required')
      setIsLoading(false)
      return
    }


    try {
      const requestBody = {
        email: formData.email,
        includeTrial: includeTrial
      }
      
      // Include affiliate code if available
      if (affiliateCode) {
        requestBody.affiliateCode = affiliateCode
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()

      if (data.success) {
        // Redirect to Stripe Checkout
        window.location.href = data.url
      } else {
        setError(data.message || 'Failed to create checkout session')
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Stripe checkout error:', error)
      setError('Failed to start checkout process. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/IAS-C-logo-blue.png" 
            alt="Impact Accelerator Circle" 
            className="w-32 h-32 mx-auto mb-6 logo-container"
            style={{ width: '128px', height: 'auto' }}
          />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Join Impact Accelerator</h1>
          <p className="text-gray-600">Create your account and start your journey</p>
        </div>

        {/* Login/Signup Toggle (temporarily hidden) */}
        <div className="hidden bg-white rounded-2xl p-1 mb-6 shadow-sm">
          <Link
            to="/login"
            className="flex-1 py-3 px-4 text-center rounded-xl text-gray-600 hover:text-gray-900 font-medium transition-colors"
          >
            Sign In
          </Link>
          <Link
            to="/register"
            className="flex-1 py-3 px-4 text-center rounded-xl bg-blue-600 text-white font-medium shadow-sm"
          >
            Sign Up
          </Link>
        </div>


        {/* Registration Form */}
        <Card className="rounded-3xl shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-gray-900">
              {includeTrial ? 'Start Your 14 Day Free Trial' : 'Join Impact Accelerator Circle'}
            </CardTitle>
            <CardDescription className="text-gray-600">
              {includeTrial 
                ? 'Free for 14 days, then $1k/month. Cancel anytime.'
                : '$1k/month. Cancel anytime. 30-day money-back guarantee.'
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleStripeCheckout} className="space-y-6">
              {error && (
                <Alert variant="destructive" className="rounded-2xl border-red-200 bg-red-50">
                  <div className="w-4 h-4 bg-red-600 rounded-full" />
                  <AlertDescription className="text-red-700">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold text-gray-700">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="rounded-2xl h-12 pl-10"
                    placeholder="name@company.com"
                    required
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  After checkout, you'll receive an email to activate your account and set your password.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white h-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  {isLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Starting Checkout...</span>
                    </div>
                  ) : (
                    <>
                      <CreditCard className="mr-2 w-4 h-4" />
                      {includeTrial ? 'Start Free Trial' : 'Start Subscription'}
                    </>
                  )}
                </Button>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="text-center mt-8 space-y-2">
          {/* TODO: Re-enable when bug report backend is implemented
          <Link to="/report-bug" className="block text-sm text-gray-500 hover:text-gray-700 transition-colors">
            Report a Bug
          </Link>
          */}
          <p className="text-xs text-gray-400">
            © 2025 Impact Accelerator Circle. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage

